import { getApiUrl, setAuthToken, registerAnonymousReminter } from "@/lib/query-client";
import { takeReaderBootToken } from "@/lib/readerBootstrap";

/**
 * Mint a server-issued demo session token for anonymous/demo visitors.
 *
 * The server resolves this token to demo-user-001 (server/middleware.ts demo
 * fallback), so every existing requireAuth/requireAuthOrApiKey floor endpoint
 * returns real data to a visitor who "looked around first" — no per-endpoint
 * changes, and the demo identity stays read-only (requireNotDemo guards key
 * minting, account connect, delete-account on the server).
 *
 * Returns the token on success, null on any failure (offline, 429 from the
 * per-IP auth rate limit, server error). Never throws — a failed mint just
 * leaves the visitor on the anonymous 401 path (existing behavior).
 *
 * ── 🔴 WHY THE TIMEOUT IS LOad-BEARING (2026-08-30) ─────────────────────────
 * This function had no timeout, and React Native's `fetch` has no default one:
 * against a stalled connection the promise never settles — it does not reject,
 * so the `catch` below never runs and "never throws" was not the same as
 * "always returns".
 *
 * Its one caller made that fatal. `useAuth.tsx`'s `bootAsGuest()` is
 *
 *     try { await mintDemoToken(); }
 *     finally { markAuthResolved(); loadStoredUser(); }
 *
 * and `loadStoredUser()` is the ONLY caller of `setIsLoading(false)`. A hang
 * therefore pinned `isLoading` true forever, and `AuthScreen`'s
 * `loading = isAuthLoading || …` disables every control on the screen — so
 * "Sign Up with Email" was permanently dead and its handler never ran.
 *
 * Measured, not theorised: `auth.email_started` had NEVER been recorded on
 * Android in the entire event table, while `auth.screen_viewed` fired normally.
 * The screen rendered; nothing on it could be pressed.
 *
 * Note the 3s "safety" timeout in `useAuth` did not save it — that watchdog
 * fires and then calls `bootAsGuest()`, i.e. it hands control straight to the
 * unbounded operation. A watchdog that delegates to the thing it is guarding
 * against is not a watchdog.
 */
const MINT_TIMEOUT_MS = 8000;

export async function mintDemoToken(): Promise<string | null> {
  const prefetched = await takeReaderBootToken();
  if (prefetched) {
    if (prefetched.token) setAuthToken(prefetched.token);
    return prefetched.token;
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MINT_TIMEOUT_MS);
  try {
    const res = await fetch(`${getApiUrl()}/api/auth/demo-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { token?: string };
    if (typeof data.token === "string" && data.token.length > 0) {
      setAuthToken(data.token);
      return data.token;
    }
    return null;
  } catch {
    // Includes the AbortError raised by the timeout above. Every exit from this
    // function now returns, which is what the caller's `finally` depends on.
    return null;
  } finally {
    clearTimeout(timer);
  }
}
/*
  Wire the re-mint path for anonymous visitors.

  Registered rather than imported by `query-client.ts`: that module cannot
  import this one (this file already imports `getApiUrl`/`setAuthToken` from
  it), so the dependency is inverted here. `useAuth.tsx` imports this module
  during boot, which is what makes the registration run.
*/
registerAnonymousReminter(mintDemoToken);
